import type { Nullable } from '@iheartjane/types';
import { useCallback, useMemo } from 'react';

import type { ArrayFilterValue, CurrentSort } from '@jane/search/types';
import { makeCurrentSort } from '@jane/search/types';
import {
  buildOptionalFilterString,
  mapCurrentSortToSearchSort,
  updateSortSuffix,
} from '@jane/search/util';
import type { Store } from '@jane/shared/types';

import type { ProductSort } from '../../../types';
import { makeDefaultWithCustomLabel, makeSortOptions } from '../../../util';
import { useSearchContext } from '../SearchProvider';

export interface ProductSortArgs {
  defaultSortName: Nullable<string>;
  hasThcProducts?: boolean;
  optionalFilters?: Store['optional_filters'];
  showAlphabeticalSort?: boolean;
  store: Store;
}

export const useProductSort = ({
  defaultSortName,
  hasThcProducts = false,
  showAlphabeticalSort = true,
  store,
}: ProductSortArgs): ProductSort => {
  const { searchState, setSearchState } = useSearchContext();

  const setCurrentSort = useCallback(
    (newSort?: CurrentSort) => {
      setSearchState({ currentSort: newSort });
    },
    [setSearchState]
  );

  const { optional_filters: optionalFilters } = store || {};

  const optionalFiltersString = optionalFilters
    ? Object.entries(optionalFilters).reduce(buildOptionalFilterString, '')
    : undefined;

  const sortOptions = useMemo(
    () =>
      makeSortOptions({
        defaultOption: makeDefaultWithCustomLabel(defaultSortName),
        hasOptionalFilters: !!optionalFiltersString,
        showAlphabeticalSort,
        showThcOptions: hasThcProducts,
      }),
    [
      defaultSortName,
      hasThcProducts,
      optionalFiltersString,
      showAlphabeticalSort,
    ]
  );

  const currentSort =
    searchState.currentSort || makeCurrentSort(sortOptions[0]);

  const weightFilters =
    (searchState.filters?.available_weights as ArrayFilterValue[]) || [];

  const searchSort = useMemo(() => {
    const sortWithWeights = {
      ...currentSort,
      suffix: updateSortSuffix(currentSort.suffix, weightFilters),
    };
    return mapCurrentSortToSearchSort(sortWithWeights);
  }, [currentSort, weightFilters]);

  return {
    currentSort,
    optionalFiltersString,
    searchSort,
    setCurrentSort,
    sortOptions,
  };
};
